import { useCallback, useEffect, useRef, useState } from 'react'
import { RequestTimeoutError } from '@/core/api/http'
import {
  loadPersonalDetails,
  updateParticipantStammdaten,
  type Participant,
  type ParticipantStammdaten,
  type TeilnahmeZeitraum,
} from '@/core/data/coachings'
import { useApp, useZurueckgezogenRow } from '@/core/state/appStore'
import { currentVariant } from '@/core/data/session'
import { isCoachOf } from '@/core/lib/orgMembers'
import { descriptionFor } from '@/variants'
import { TextField } from '@/core/components/form/TextField'
import { FormRenderer } from '@/core/components/form/FormRenderer'
import { initialFormValues, updateFormField, type FormValues } from '@/core/components/form/formModel'
import { teilnehmerDetailsForm } from '@/core/templates'
import type { FormDefinition } from '@/core/templates/types'
import type { FieldError } from '@/core/lib/fieldError'
import { useAutosave } from '@/core/hooks/useAutosave'
import { completeStammdatenAsStaff } from '@/core/lib/stammdatenStaffComplete'
import { isPendingKundennummer, KUNDENNUMMER_PENDING_TEXT } from '@/core/lib/kundennummer'
import { ModalHeader } from '../ModalHeader'
import { RueckzugVermerk } from './RueckzugVermerk'
import { Callout, FormSection, LoadingState, ScrollBar } from 'admination-react'

function displayDate(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '')
  if (!m) return ''
  return `${m[3]}.${m[2]}.${m[1]}`
}

function ReadField({ label, value, frozen = false }: { label: string; value: string | null | undefined; frozen?: boolean }) {
  if (!value) return null
  return <TextField label={label} value={value} onChange={() => {}} readOnly={frozen ? 'frozen' : true} />
}

function TeilnahmeSection({ zeitraum }: { zeitraum: TeilnahmeZeitraum }) {
  const von = displayDate(zeitraum.zuweisungBeginn)
  const bis = displayDate(zeitraum.zuweisungEnde)
  const zuweisung = von && bis ? `${von} – ${bis}` : ''
  const teilnahmebeginn = displayDate(zeitraum.teilnahmebeginn)
  if (!zuweisung && !teilnahmebeginn) return null
  return (
    <div className="section" data-testid="teilnahme-zeitraum">
      <h3 className="section-header">Teilnahme</h3>
      <div className="section-body">
        <ReadField label="Zuweisungszeitraum" value={zuweisung} frozen />
        <ReadField label="Teilnahmebeginn" value={teilnahmebeginn} frozen />
      </div>
    </div>
  )
}

function str(section: Record<string, unknown> | undefined, key: string): string {
  const v = section?.[key]
  return typeof v === 'string' ? v : ''
}

const LOAD_FAILED_MESSAGE = "Die Daten konnten nicht geladen werden. Bitte versuchen Sie es in einem Moment erneut."

const TEILNEHMER_SECTION = 'Teilnehmer'
const KUNDENNUMMER_FIELD = `${TEILNEHMER_SECTION}.Kundennummer`
const KUNDENNUMMER_READONLY = new Set([KUNDENNUMMER_FIELD])

const PARTICIPANT_FIELDS = [
  ["Nachname", 'nachname'],
  ["Vorname", 'vorname'],
  ["Kundennummer", 'kundennummer'],
  ["Geburtsdatum", 'geburtsdatum'],
  ["Straße", 'strasse'],
  ["Hausnummer", 'hausnummer'],
  ["PLZ", 'plz'],
  ["Ort", 'ort'],
  ["Telefon", 'phone'],
  ["E-Mail", 'email'],
] as const

const VERGABE_READ_FIELDS = [
  ["Handy", 'mobil'],
  ["Adresszusatz", 'adresszusatz'],
  ["Geschlecht", 'geschlecht'],
] as const

const vergabeDetailsForm: FormDefinition = {
  sections: {
    ...teilnehmerDetailsForm.sections,
    Teilnehmer: {
      ...teilnehmerDetailsForm.sections.Teilnehmer,
      fields: [
        ...teilnehmerDetailsForm.sections.Teilnehmer.fields,
        ...VERGABE_READ_FIELDS.map(([label]) => ({ id: `Teilnehmer.${label}`, control: 'display' as const })),
      ],
    },
  },
}

function participantValue(participant: Participant | null, key: typeof PARTICIPANT_FIELDS[number][1]): string {
  const value = participant?.[key] ?? ''
  return key === 'kundennummer' && isPendingKundennummer(value) ? KUNDENNUMMER_PENDING_TEXT : value
}

function valuesForParticipant(participant: Participant): Record<string, unknown> {
  return { [TEILNEHMER_SECTION]: Object.fromEntries([
    ...PARTICIPANT_FIELDS.map(([label, key]) => [label, participantValue(participant, key)]),
    ...VERGABE_READ_FIELDS.map(([label, key]) => [label, participant[key] ?? '']),
  ]) }
}

function patchFromValues(values: FormValues): ParticipantStammdaten {
  return Object.fromEntries(PARTICIPANT_FIELDS.map(([label, key]) => {
    const value = str(values[TEILNEHMER_SECTION], label)
    return [key === 'phone' ? 'telefon' : key, ['nachname', 'vorname', 'kundennummer', 'email'].includes(key) ? value.trim() : value]
  }))
}

export function PersonalDetails({ coachingId }: { coachingId: string | null }) {
  const { dispatch, coachings, identity: viewer } = useApp()
  const scrollerRef = useRef<HTMLElement>(null)
  const [details, setDetails] = useState<Awaited<ReturnType<typeof loadPersonalDetails>> | null>(null)
  const { participant = null, meta = null } = details ?? {}
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setDetails(null)
    setLoadError(null)
    if (!coachingId) return
    loadPersonalDetails(coachingId)
      .then((next) => { if (!cancelled) setDetails(next) })
      .catch((e) => {
        console.error("Persönliche Daten laden fehlgeschlagen:", e)
        if (!cancelled) setLoadError(e instanceof RequestTimeoutError ? e.message : LOAD_FAILED_MESSAGE)
      })
    return () => {
      cancelled = true
    }
  }, [coachingId])

  const stageLabel = meta?.stage
    ? (descriptionFor(currentVariant()).columns.find((c) => c.id === meta.stage)?.dbName ?? meta.stage)
    : null

  const zurueckgezogen = useZurueckgezogenRow(coachingId)
  const coaching = coachings.find((c) => c.id === coachingId)
  const canEdit =
    !!viewer && !zurueckgezogen && (viewer.isOwner || isCoachOf({ userId: viewer.userId }, coaching?.coachId ?? null, coaching?.secondCoachId))

  const [values, setValues] = useState<FormValues | null>(null)
  const lastCoachingId = useRef<string | null | undefined>(undefined)

  const pendingKundennummer = useRef(false)

  useEffect(() => {
    if (!participant) return
    if (lastCoachingId.current !== coachingId) {
      lastCoachingId.current = coachingId
      pendingKundennummer.current = isPendingKundennummer(participant.kundennummer)
      const v = initialFormValues(currentVariant() === 'vergabe' ? vergabeDetailsForm : teilnehmerDetailsForm, valuesForParticipant(participant))
      setValues(v)
    }
  }, [participant, coachingId])

  const setField = useCallback((path: string, v: unknown) => {
    setValues((prev) => updateFormField(prev, path, v))
  }, [])

  const save = useCallback(
    (v: FormValues | null, baseline: FormValues | null | undefined, assertCurrent: () => void) => {
      if (!v || !coachingId || !canEdit) return
      const current = patchFromValues(v)
      const previous = baseline ? patchFromValues(baseline) : undefined
      const changed: ParticipantStammdaten = Object.fromEntries(Object.entries(current).filter(([key, value]) => previous?.[key as keyof ParticipantStammdaten] !== value))

      if (pendingKundennummer.current) delete changed.kundennummer
      if (Object.keys(changed).length === 0) return
      return updateParticipantStammdaten(coachingId, changed)
        .then(() => {
          assertCurrent()

          return completeStammdatenAsStaff(coachingId).catch((e) => {
            console.error("Stammdaten-Vollständigkeitsprüfung fehlgeschlagen:", e)
            throw e
          })
        })
        .catch((e) => {
          console.error("Teilnehmer speichern fehlgeschlagen:", e)
          throw e
        })
    },
    [coachingId, canEdit],
  )

  const { status } = useAutosave(values, values !== null && !!coachingId && canEdit, save, 250, { form: "PersonalDetails", record: coachingId ?? undefined, label: "Teilnehmerdaten", recover: setValues })
  const errors = new Map<string, FieldError>()
  if (status === 'error' && values) {
    for (const field of ['Vorname', 'Nachname', 'Geburtsdatum']) {
      if (!str(values[TEILNEHMER_SECTION], field).trim()) errors.set(`${TEILNEHMER_SECTION}.${field}`, { kind: 'missing' })
    }
  }

  return (
    <>
      <ModalHeader onClose={() => dispatch({ type: 'CLOSE' })} />
      <main className="form" data-scroll-host ref={scrollerRef}>
        {zurueckgezogen ? <RueckzugVermerk row={zurueckgezogen} isOwner={viewer?.isOwner === true} /> : null}
        {loadError ? (
          <FormSection>
            <Callout tone="error" role="alert" title={loadError} data-testid="personal-details-ladefehler" />
          </FormSection>
        ) : coachingId && !details ? (
          <LoadingState>Daten werden geladen …</LoadingState>
        ) : (
          <>
          {canEdit && values ? (
            <FormRenderer
                definition={currentVariant() === 'vergabe' ? vergabeDetailsForm : teilnehmerDetailsForm}
                values={values}
                onChange={setField}
                errors={errors}
                field={(field) => ({
                  readOnly: (pendingKundennummer.current ? KUNDENNUMMER_READONLY : undefined)?.has(field.id),
                })}
              />
          ) : (
            <div className="section">
              <h3 className="section-header">Teilnehmer</h3>
              <div className="section-body">
                {PARTICIPANT_FIELDS.map(([label, key]) => <ReadField key={key} label={label}
                  value={key === 'geburtsdatum' ? displayDate(participant?.[key]) : participantValue(participant, key)} />)}
              </div>
            </div>
          )}
          {!canEdit && currentVariant() === 'vergabe' && <div className="section">
            <h3 className="section-header">Weitere Stammdaten</h3>
            <div className="section-body">
              {VERGABE_READ_FIELDS.map(([label, key]) => <ReadField key={key} label={label} value={participant?.[key]} />)}
            </div>
          </div>}
          <div className="section">
            <h3 className="section-header">Coaching</h3>
            <div className="section-body">
              <ReadField label="Status" value={stageLabel} frozen />
              <ReadField label="Gültigkeit Beginn" value={displayDate(meta?.gueltigkeitBeginn)} />
              <ReadField label="Gültigkeit Ende" value={displayDate(meta?.gueltigkeitEnde)} />
            </div>
          </div>
          {meta?.teilnahme ? <TeilnahmeSection zeitraum={meta.teilnahme} /> : null}
          </>
        )}
      </main>
      <ScrollBar scrollerRef={scrollerRef} />
    </>
  )
}
